import { useCallback, useEffect, useRef, useState } from "react";

const SDK_URL = "https://sdk.mercadopago.com/js/v2";
const SCRIPT_ID = "mercadopago-sdk-v2";

/** Carrega o SDK uma única vez, mesmo com várias telas montadas ao mesmo tempo. */
let sdkPromise = null;

const loadSdk = () => {
  if (window.MercadoPago) return Promise.resolve(window.MercadoPago);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      existing.addEventListener("load", () => resolve(window.MercadoPago));
      existing.addEventListener("error", () => reject(new Error("Falha ao carregar o SDK do Mercado Pago")));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => resolve(window.MercadoPago);
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error("Falha ao carregar o SDK do Mercado Pago"));
    };
    document.body.appendChild(script);
  });

  return sdkPromise;
};

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

/**
 * Integra o SDK v2 do Mercado Pago.
 *
 * O número do cartão, CVV e validade são enviados direto do navegador para o
 * Mercado Pago, que devolve um token de uso único. É esse token — e nada mais —
 * que chega ao nosso backend.
 *
 * @param {string} publicKey Chave pública da conta (vem de /billing/catalog).
 */
export const useMercadoPago = (publicKey) => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const mpRef = useRef(null);

  useEffect(() => {
    if (!publicKey) return undefined;

    let cancelled = false;

    loadSdk()
      .then((MercadoPago) => {
        if (cancelled) return;
        mpRef.current = new MercadoPago(publicKey, { locale: "pt-BR" });
        setReady(true);
        setError(null);
      })
      .catch((sdkError) => {
        if (cancelled) return;
        setError(sdkError.message);
        setReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [publicKey]);

  /** Descobre bandeira e emissor a partir dos 6 primeiros dígitos do cartão. */
  const identifyCard = useCallback(async (cardNumber) => {
    const bin = onlyDigits(cardNumber).slice(0, 6);
    if (!mpRef.current || bin.length < 6) return null;

    const response = await mpRef.current.getPaymentMethods({ bin });
    const method = response?.results?.[0];
    if (!method) return null;

    return {
      bin,
      paymentMethodId: method.id,
      paymentTypeId: method.payment_type_id,
      thumbnail: method.thumbnail,
      name: method.name,
      issuerId: method.issuer?.id ? String(method.issuer.id) : null,
      // Regras de formatação vindas do próprio MP (tamanho do CVV, do número).
      settings: method.settings?.[0] || null,
    };
  }, []);

  /** Opções de parcelamento reais para o valor informado. */
  const fetchInstallments = useCallback(async ({ cardNumber, amountCents }) => {
    const bin = onlyDigits(cardNumber).slice(0, 6);
    if (!mpRef.current || bin.length < 6 || !amountCents) return [];

    const response = await mpRef.current.getInstallments({
      amount: String(Number(amountCents) / 100),
      bin,
      paymentTypeId: "credit_card",
    });

    return response?.[0]?.payer_costs || [];
  }, []);

  /**
   * Troca os dados do cartão por um token de uso único.
   * @returns {Promise<string>} id do token
   */
  const createCardToken = useCallback(async (card) => {
    if (!mpRef.current) throw new Error("SDK do Mercado Pago ainda não carregou.");

    const expiration = String(card.expiration || "").split("/");
    const month = onlyDigits(card.expirationMonth ?? expiration[0]);
    let year = onlyDigits(card.expirationYear ?? expiration[1] ?? "");
    if (year.length === 2) year = `20${year}`;

    const response = await mpRef.current.createCardToken({
      cardNumber: onlyDigits(card.cardNumber),
      cardholderName: String(card.cardholderName || "").trim(),
      cardExpirationMonth: month,
      cardExpirationYear: year,
      securityCode: onlyDigits(card.securityCode),
      identificationType: card.identificationType || "CPF",
      identificationNumber: onlyDigits(card.identificationNumber),
    });

    if (!response?.id) {
      throw new Error("Não foi possível validar os dados do cartão.");
    }

    return response.id;
  }, []);

  return { ready, error, identifyCard, fetchInstallments, createCardToken };
};

export default useMercadoPago;
